const DEFAULT_MAX_ENTRIES = 48;

function safeParse(value) {
  try { return JSON.parse(value); } catch { return null; }
}

export function createPerformanceCache({
  namespace = 'rv-perf-v1',
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  now = () => Date.now(),
  maxEntries = DEFAULT_MAX_ENTRIES,
} = {}) {
  const memory = new Map();
  const inflight = new Map();
  const storageKey = key => `${namespace}:${key}`;

  function readRecord(key) {
    if (memory.has(key)) return memory.get(key);
    if (!storage) return null;
    const record = safeParse(storage.getItem(storageKey(key)));
    if (!record || typeof record.savedAt !== 'number' || !('value' in record)) return null;
    memory.set(key, record);
    return record;
  }

  function peek(key, maxAge = Infinity) {
    const record = readRecord(key);
    if (!record || now() - record.savedAt > maxAge) return undefined;
    return record.value;
  }

  function set(key, value) {
    const record = { savedAt: now(), value };
    memory.delete(key);
    memory.set(key, record);
    if (memory.size > maxEntries) memory.delete(memory.keys().next().value);
    if (storage) {
      try { storage.setItem(storageKey(key), JSON.stringify(record)); }
      catch {
        // Storage quota is optional. The in-memory cache remains available.
      }
    }
    return value;
  }

  function age(key) {
    const record = readRecord(key);
    return record ? Math.max(0, now() - record.savedAt) : Infinity;
  }

  function coordinate(key, loader) {
    if (inflight.has(key)) return inflight.get(key);
    const task = Promise.resolve().then(loader).finally(() => inflight.delete(key));
    inflight.set(key, task);
    return task;
  }

  function markAllStale() {
    const staleAt = now() - 60_000;
    memory.forEach((record, key) => {
      record.savedAt = Math.min(record.savedAt, staleAt);
      if (storage) {
        try { storage.setItem(storageKey(key), JSON.stringify(record)); } catch { }
      }
    });
    if (!storage) return;
    try {
      const prefix = `${namespace}:`;
      const keys = [];
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key?.startsWith(prefix)) keys.push(key);
      }
      keys.forEach(fullKey => {
        const record = safeParse(storage.getItem(fullKey));
        if (!record || typeof record !== 'object') return;
        record.savedAt = Math.min(Number(record.savedAt) || 0, staleAt);
        storage.setItem(fullKey, JSON.stringify(record));
      });
    } catch { }
  }

  function markMatchingStale(matcher) {
    if (typeof matcher !== 'function') return markAllStale();
    const staleAt = now() - 60_000;
    memory.forEach((record, key) => {
      if (!matcher(key)) return;
      record.savedAt = Math.min(record.savedAt, staleAt);
      if (storage) {
        try { storage.setItem(storageKey(key), JSON.stringify(record)); } catch { }
      }
    });
    if (!storage) return;
    try {
      const prefix = `${namespace}:`;
      for (let index = 0; index < storage.length; index += 1) {
        const fullKey = storage.key(index);
        if (!fullKey?.startsWith(prefix)) continue;
        const key = fullKey.slice(prefix.length);
        if (!matcher(key)) continue;
        const record = safeParse(storage.getItem(fullKey));
        if (!record || typeof record !== 'object') continue;
        record.savedAt = Math.min(Number(record.savedAt) || 0, staleAt);
        storage.setItem(fullKey, JSON.stringify(record));
      }
    } catch { }
  }

  function clear() {
    memory.clear();
    inflight.clear();
    if (!storage) return;
    try {
      const prefix = `${namespace}:`;
      const keys = [];
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key?.startsWith(prefix)) keys.push(key);
      }
      keys.forEach(key => storage.removeItem(key));
    } catch { }
  }

  return { peek, set, age, coordinate, markAllStale, markMatchingStale, clear };
}

export function stableDataSignature(value) {
  if (value == null) return String(value);
  try { return JSON.stringify(value); }
  catch { return `${typeof value}:${String(value)}`; }
}

export function createBoundedQueue({ concurrency = 3 } = {}) {
  const pending = [];
  let active = 0;

  function drain() {
    while (active < concurrency && pending.length) {
      const item = pending.shift();
      active += 1;
      Promise.resolve().then(item.task).then(item.resolve, item.reject).finally(() => {
        active -= 1;
        drain();
      });
    }
  }

  function add(task) {
    return new Promise((resolve, reject) => {
      pending.push({ task, resolve, reject });
      drain();
    });
  }

  return { add, get active() { return active; }, get pending() { return pending.length; } };
}
